import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Users, ChevronRight, Loader2, Trash2 } from "lucide-react";

export default function Companies() {
  const nav = useNavigate();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const template = async () => {
    try {
      const r = await api.get("/companies/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = "cevre-firma-sablonu.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Şablon indirilemedi"); }
  };
  const importExcel = async (commit = false) => {
    if (!bulkFile) return;
    setBulkBusy(true);
    const data = new FormData(); data.append("file", bulkFile);
    try {
      const r = await api.post(`/companies/import/${commit ? "commit" : "preview"}`, data);
      if (commit) {
        toast.success(`${r.data.created} firma eklendi, ${r.data.skipped} tekrar atlandı`);
        setBulkOpen(false); setPreview(null); setBulkFile(null); load();
      } else setPreview(r.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };
  const [companies, setCompanies] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", phone: "", email: "", address: "" });

  const load = () => api.get("/companies").then((r) => setCompanies(r.data));
  useEffect(() => { load(); }, []);

  const purge = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`"${c.name}" firması ve TÜM belgeleri kalıcı olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try { await api.delete(`/companies/${c.id}?purge=true`); toast.success("Firma kalıcı olarak silindi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Firma ünvanı zorunludur"); return; }
    setSaving(true);
    try {
      await api.post("/companies", form);
      toast.success("Firma oluşturuldu");
      setOpen(false); setForm({ name: "", contact_person: "", phone: "", email: "", address: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!companies) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>;

  return (
    <div className="fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[#0a192f]">Firmalar</h1>
          <p className="mt-1 text-slate-500">Danışmanlık verdiğiniz firmaları yönetin</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-company-button">
              <Plus className="mr-2 h-4 w-4" /> Yeni Firma
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Yeni Firma Ekle</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Firma Ünvanı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Yetkili Kişi</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div><Label>E-posta</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Adres</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="company-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Button variant="outline" onClick={() => { setBulkOpen(true); setBulkFile(null); setPreview(null); }}>Excel ile Toplu Firma Ekle</Button>
      <Dialog open={bulkOpen} onOpenChange={(v) => { if (!bulkBusy) setBulkOpen(v); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Excel ile Toplu Firma Ekle</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Firma adı zorunludur. Diğer alanlar isteğe bağlıdır. Mevcut firmalar güncellenmez; tekrarlar atlanır. Müşteri hesabı oluşturulmaz.</p>
          <Button variant="outline" onClick={template}>Excel Şablonunu İndir</Button>
          <Input type="file" accept=".xlsx" disabled={bulkBusy} onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setPreview(null); }} />
          <p className="text-xs text-slate-500">En fazla 1000 firma, 5 MB. Telefonları metin olarak girin; başlıkları değiştirmeyin.</p>
          <Button disabled={!bulkFile || bulkBusy} onClick={() => importExcel(false)}>{bulkBusy ? "İşleniyor…" : "Önizleme"}</Button>
          {preview && <>
            <p className="text-sm">{preview.ready} firma eklenebilir. Hatalı satır varsa dosyayı düzeltip yeniden yükleyin.</p>
            <div className="max-h-72 overflow-auto"><table className="w-full text-sm"><thead><tr><th className="text-left">Satır</th><th className="text-left">Firma</th><th className="text-left">Yetkili / Telefon / E-posta / Adres</th><th className="text-left">Durum</th></tr></thead><tbody>
              {preview.rows.map((r) => <tr key={r.row} className="border-t"><td className="p-2">{r.row}</td><td className="p-2">{r.name || "—"}</td><td className="p-2">{[r.contact_person, r.phone, r.email, r.address].filter(Boolean).join(" · ")}</td><td className={`p-2 ${r.status === "error" ? "text-red-600" : r.status === "ready" ? "text-emerald-700" : "text-amber-700"}`}>{r.message}</td></tr>)}
            </tbody></table></div>
            <Button className="bg-emerald-600" disabled={bulkBusy || !preview.ready || preview.rows.some((r) => r.status === "error")} onClick={() => importExcel(true)}>Onayla ve {preview.ready} Firmayı Ekle</Button>
          </>}
        </DialogContent>
      </Dialog>

      {companies.length === 0 ? (
        <Card className="p-12 text-center text-slate-400" data-testid="companies-empty">
          <Building2 className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          Henüz firma eklenmedi. "Yeni Firma" ile başlayın.
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((c) => (
            <Card key={c.id} onClick={() => nav(`/admin/firmalar/${c.id}`)} data-testid={`company-card-${c.id}`}
              className="group cursor-pointer p-5 transition-all hover:border-emerald-300 hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0a192f] text-white">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => purge(e, c)} data-testid={`company-purge-${c.id}`}
                    className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  <ChevronRight className="h-5 w-5 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-emerald-600" />
                </div>
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-[#0a192f]">{c.name}</h3>
              {c.contact_person && <p className="text-sm text-slate-500">{c.contact_person}</p>}
              <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                <Users className="h-3.5 w-3.5" /> {c.user_count} müşteri hesabı
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
